import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetUserTransactionsQuery } from '../services/auth/authApi';
import Loading from '../components/Loading';

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const formatApiDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const today = new Date();
const oneMonthAgo = new Date();
oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

const StatementPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [fromDate, setFromDate] = useState(formatApiDate(oneMonthAgo));
  const [toDate, setToDate]     = useState(formatApiDate(today));
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  const { data: transactionsData, isLoading, error } = useGetUserTransactionsQuery({
    userId,
    page: currentPage,
    limit: itemsPerPage,
  });

  const transactions = transactionsData?.data || [];
  const pagination   = transactionsData?.pagination || {};
  const totalPages   = pagination.totalPages || 1;

  return (
    <div className="w-full animate-fadeIn">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-[#0c1524] to-[#162032] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-teal-400 rounded-full" />
            <h1 className="text-white font-semibold text-base tracking-wide">
              List Of All Transactions
              {pagination.total != null && (
                <span className="ml-2 text-teal-400/80 text-sm font-normal">({pagination.total})</span>
              )}
            </h1>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-all"
          >
            <BackIcon />
            Back
          </button>
        </div>

        {/* Filters */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
              className="text-sm text-slate-700 focus:outline-none bg-transparent"
            />
            <span className="text-slate-400 text-xs">→</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
              className="text-sm text-slate-700 focus:outline-none bg-transparent"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-16">
              <Loading />
            </div>
          ) : error ? (
            <div className="py-10 text-center text-red-500 text-sm">
              {error.message || 'Failed to load transactions'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0F2327] text-white">
                  {['Date', 'Description', 'Prev Bal', 'CR', 'DR', 'Comm+', 'Comm-', 'Balance'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap border-b border-[#1a3a40]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-16 text-center text-slate-400 text-sm">
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  transactions.map((tx, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{tx.date}</td>
                      <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{tx.description}</td>
                      <td className="px-3 py-2.5 text-slate-700">{tx.prevBal}</td>
                      <td className="px-3 py-2.5 font-medium text-emerald-600">{tx.cr}</td>
                      <td className="px-3 py-2.5 font-medium text-red-500">{tx.dr}</td>
                      <td className="px-3 py-2.5 font-medium text-emerald-600">{tx.commPlus}</td>
                      <td className="px-3 py-2.5 font-medium text-red-500">{tx.commMinus}</td>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{tx.balance}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {transactions.length > 0 && (
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="px-2 py-1.5 text-sm bg-white border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:border-teal-400"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>

            <p className="text-xs text-slate-500">
              Page <span className="font-semibold text-slate-700">{currentPage}</span> of{' '}
              <span className="font-semibold text-slate-700">{totalPages}</span>
              {pagination.total != null && (
                <> · <span className="font-semibold text-slate-700">{pagination.total}</span> transactions</>
              )}
            </p>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                ← Prev
              </button>

              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let p;
                if (totalPages <= 5)            p = i + 1;
                else if (currentPage <= 3)       p = i + 1;
                else if (currentPage >= totalPages - 2) p = totalPages - 4 + i;
                else                             p = currentPage - 2 + i;
                return (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                      currentPage === p
                        ? 'bg-[#0c1524] text-white'
                        : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage >= totalPages}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Next →
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default StatementPage;
